import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const NotificationSection = () => {
  const [notifications, setNotifications] = useState({
    priceAlerts: true,
    portfolioUpdates: true,
    marketNews: false,
    systemNotifications: true,
    emailDigest: true,
    smsAlerts: false,
    pushNotifications: true
  });

  const [alertSettings, setAlertSettings] = useState({
    priceChangeThreshold: '5',
    portfolioChangeThreshold: '10',
    emailFrequency: 'daily',
    digestTime: '09:00'
  });

  const [isLoading, setIsLoading] = useState(false);

  const frequencyOptions = [
    { value: 'realtime', label: 'Real-time' },
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' }
  ];

  const timeOptions = [
    { value: '06:00', label: '6:00 AM' },
    { value: '09:00', label: '9:00 AM' },
    { value: '12:00', label: '12:00 PM' },
    { value: '18:00', label: '6:00 PM' },
    { value: '21:00', label: '9:00 PM' }
  ];

  const handleNotificationToggle = (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleAlertSettingChange = (key, value) => {
    setAlertSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsLoading(false);
    console.log('Notification settings saved');
  };

  const notificationCategories = [
    {
      title: 'Price Alerts',
      description: 'Get notified when stock prices change significantly',
      key: 'priceAlerts',
      icon: 'TrendingUp',
      color: 'text-success'
    },
    {
      title: 'Portfolio Updates',
      description: 'Receive updates about your portfolio performance',
      key: 'portfolioUpdates',
      icon: 'PieChart',
      color: 'text-primary'
    },
    {
      title: 'Market News',
      description: 'Stay informed about market trends and news',
      key: 'marketNews',
      icon: 'Newspaper',
      color: 'text-accent'
    },
    {
      title: 'System Notifications',
      description: 'Important system updates and maintenance alerts',
      key: 'systemNotifications',
      icon: 'Bell',
      color: 'text-warning'
    }
  ];

  const deliveryMethods = [
    {
      title: 'Email Digest',
      description: 'Receive a daily summary of your portfolio and market updates',
      key: 'emailDigest',
      icon: 'Mail',
      color: 'text-secondary'
    },
    {
      title: 'SMS Alerts',
      description: 'Get critical alerts via text message',
      key: 'smsAlerts',
      icon: 'MessageSquare',
      color: 'text-success'
    },
    {
      title: 'Push Notifications',
      description: 'Receive instant notifications in your browser',
      key: 'pushNotifications',
      icon: 'Smartphone',
      color: 'text-primary'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Notification Preferences */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Bell" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Notification Preferences</h2>
            <p className="text-sm text-muted-foreground">Choose what notifications you want to receive</p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Notification Types</h3>
          {notificationCategories.map((category) => (
            <div key={category.key} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-background rounded-lg flex items-center justify-center">
                  <Icon name={category.icon} size={20} className={category.color} />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground">{category.title}</h4>
                  <p className="text-xs text-muted-foreground">{category.description}</p>
                </div>
              </div>
              <Checkbox
                checked={notifications[category.key]}
                onChange={() => handleNotificationToggle(category.key)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Alert Thresholds */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Alert Thresholds</h2>
            <p className="text-sm text-muted-foreground">Set when you want to be notified about changes</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Price Change Threshold"
            type="number"
            value={alertSettings.priceChangeThreshold}
            onChange={(e) => handleAlertSettingChange('priceChangeThreshold', e.target.value)}
            description="Notify when stock price changes by this percentage"
            disabled={!notifications.priceAlerts}
          />
          
          <Input
            label="Portfolio Change Threshold"
            type="number"
            value={alertSettings.portfolioChangeThreshold}
            onChange={(e) => handleAlertSettingChange('portfolioChangeThreshold', e.target.value)}
            description="Notify when portfolio value changes by this percentage"
            disabled={!notifications.portfolioUpdates}
          />
        </div>
      </div>

      {/* Delivery Methods */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Send" size={20} className="text-secondary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Delivery Methods</h2>
            <p className="text-sm text-muted-foreground">Choose how you want to receive notifications</p>
          </div>
        </div>

        <div className="space-y-4">
          {deliveryMethods.map((method) => (
            <div key={method.key} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-background rounded-lg flex items-center justify-center">
                  <Icon name={method.icon} size={20} className={method.color} />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground">{method.title}</h4>
                  <p className="text-xs text-muted-foreground">{method.description}</p>
                </div>
              </div>
              <Checkbox
                checked={notifications[method.key]}
                onChange={() => handleNotificationToggle(method.key)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Email Settings */}
      <div className="card-elevated p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Mail" size={20} className="text-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Email Settings</h2>
            <p className="text-sm text-muted-foreground">Configure your email notification preferences</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Email Frequency"
            options={frequencyOptions}
            value={alertSettings.emailFrequency}
            onChange={(value) => handleAlertSettingChange('emailFrequency', value)}
            disabled={!notifications.emailDigest}
            description="How often you want to receive email updates"
          />
          
          <Select
            label="Digest Time"
            options={timeOptions}
            value={alertSettings.digestTime}
            onChange={(value) => handleAlertSettingChange('digestTime', value)}
            disabled={!notifications.emailDigest || alertSettings.emailFrequency === 'realtime'}
            description="When you want to receive daily digest emails"
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          variant="default"
          onClick={handleSaveSettings}
          loading={isLoading}
          iconName="Save"
          iconPosition="left"
        >
          Save Notification Settings
        </Button>
      </div>
    </div>
  );
};

export default NotificationSection;